import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { LANGUAGES, LANGUAGE_NAMES } from '../constants/languages';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.herbieterms.com';

const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

const getMultipartHeaders = () => ({
    'Content-Type': 'multipart/form-data',
    'x-api-key': process.env.REACT_APP_API_KEY,
});

function ExcelTermbaseBatch() {
    const [file, setFile] = useState(null);
    const [workbookInfo, setWorkbookInfo] = useState(null);
    const [sheetName, setSheetName] = useState('');
    const [sourceColumnIndex, setSourceColumnIndex] = useState('');
    const [sourceLanguage, setSourceLanguage] = useState('en');
    const [targetLanguages, setTargetLanguages] = useState(['da']);
    const [analysis, setAnalysis] = useState(null);
    const [selections, setSelections] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const currentSheet = useMemo(
        () => workbookInfo?.sheets?.find((sheet) => sheet.name === sheetName) || null,
        [workbookInfo, sheetName]
    );

    const availableTargets = LANGUAGES.filter((language) => language.code !== sourceLanguage);

    const resetAnalysis = () => {
        setAnalysis(null);
        setSelections({});
        setError('');
    };

    const handleFileChange = (event) => {
        const nextFile = event.target.files?.[0] || null;
        setFile(nextFile);
        setWorkbookInfo(null);
        setSheetName('');
        setSourceColumnIndex('');
        resetAnalysis();
    };

    const handleInspect = async () => {
        if (!file) {
            setError('Choose an Excel file first.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post('/excel-termbase/inspect', formData, {
                headers: getMultipartHeaders(),
            });

            const info = response.data;
            setWorkbookInfo(info);

            const firstSheet = info?.sheets?.[0];
            if (firstSheet) {
                setSheetName(firstSheet.name);
                setSourceColumnIndex(firstSheet.columns?.[0]?.index ?? '');
            }
        } catch (err) {
            console.error('Excel inspect failed:', err);
            setError(err.response?.data?.message || 'Could not inspect the Excel file.');
        } finally {
            setLoading(false);
        }
    };

    const handleSheetChange = (nextSheetName) => {
        setSheetName(nextSheetName);
        const nextSheet = workbookInfo?.sheets?.find((sheet) => sheet.name === nextSheetName);
        setSourceColumnIndex(nextSheet?.columns?.[0]?.index ?? '');
        resetAnalysis();
    };

    const handleSourceLanguageChange = (nextLanguage) => {
        setSourceLanguage(nextLanguage);
        setTargetLanguages((prev) => prev.filter((code) => code !== nextLanguage));
        resetAnalysis();
    };

    const handleTargetLanguagesChange = (event) => {
        const value = event.target.value;
        const next = typeof value === 'string' ? value.split(',') : value;
        setTargetLanguages(next.filter((code) => code !== sourceLanguage));
        resetAnalysis();
    };

    const seedSelections = (rows, targets) => {
        const seeded = {};
        rows.forEach((row) => {
            targets.forEach((language) => {
                const candidates = row.candidatesByLanguage?.[language] || [];
                if (candidates.length === 1) {
                    if (!seeded[row.rowNumber]) seeded[row.rowNumber] = {};
                    seeded[row.rowNumber][language] = candidates[0].termId;
                }
            });
        });
        return seeded;
    };

    const handleAnalyze = async () => {
        if (!file || !sheetName || sourceColumnIndex === '') {
            setError('Choose a workbook, sheet, and source column first.');
            return;
        }
        if (!targetLanguages.length) {
            setError('Choose at least one target language.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('sheetName', sheetName);
            formData.append('sourceColumnIndex', String(sourceColumnIndex));
            formData.append('sourceLanguage', sourceLanguage);
            formData.append('targetLanguages', JSON.stringify(targetLanguages));

            const response = await api.post('/excel-termbase/analyze', formData, {
                headers: getMultipartHeaders(),
            });

            setAnalysis(response.data);
            setSelections(seedSelections(response.data.rows || [], targetLanguages));
        } catch (err) {
            console.error('Excel termbase analysis failed:', err);
            setError(err.response?.data?.message || 'Could not analyze the workbook.');
        } finally {
            setLoading(false);
        }
    };

    const setChoice = (rowNumber, language, termId) => {
        setSelections((prev) => ({
            ...prev,
            [rowNumber]: {
                ...(prev[rowNumber] || {}),
                [language]: termId,
            },
        }));
    };

    const ambiguousUnresolvedCount = useMemo(() => {
        if (!analysis?.rows) return 0;
        let count = 0;
        analysis.rows.forEach((row) => {
            targetLanguages.forEach((language) => {
                const candidates = row.candidatesByLanguage?.[language] || [];
                if (candidates.length > 1 && !selections[row.rowNumber]?.[language]) {
                    count += 1;
                }
            });
        });
        return count;
    }, [analysis, selections, targetLanguages]);

    const noMatchCount = useMemo(() => {
        if (!analysis?.rows) return 0;
        let count = 0;
        analysis.rows.forEach((row) => {
            targetLanguages.forEach((language) => {
                if ((row.candidatesByLanguage?.[language] || []).length === 0) count += 1;
            });
        });
        return count;
    }, [analysis, targetLanguages]);

    const handleExport = async () => {
        if (!file || !analysis) return;
        if (ambiguousUnresolvedCount > 0) {
            setError('Choose a termbase variant for every row that has multiple candidates before exporting.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('sheetName', sheetName);
            formData.append('sourceColumnIndex', String(sourceColumnIndex));
            formData.append('sourceLanguage', sourceLanguage);
            formData.append('targetLanguages', JSON.stringify(targetLanguages));
            formData.append('selections', JSON.stringify(selections));

            const response = await api.post('/excel-termbase/export', formData, {
                headers: getMultipartHeaders(),
                responseType: 'blob',
            });

            const contentDisposition = response.headers['content-disposition'] || '';
            const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
            const filename = filenameMatch?.[1] || 'termbase-translated.xlsx';

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Excel export failed:', err);
            setError('Could not create the translated Excel file.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} mb={1.5}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Excel termbase translation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Fill Excel columns only with exact terms that already exist in the termbase. Nothing is machine-translated.
                    </Typography>
                </Box>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
                        <Button component="label" variant="outlined" size="small">
                            Choose Excel file
                            <input hidden type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                            {file ? file.name : 'No file selected'}
                        </Typography>
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleInspect}
                            disabled={!file || loading}
                        >
                            Inspect workbook
                        </Button>
                    </Stack>

                    {workbookInfo && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                                gap: 1.5,
                            }}
                        >
                            <FormControl size="small" fullWidth>
                                <InputLabel>Sheet</InputLabel>
                                <Select
                                    label="Sheet"
                                    value={sheetName}
                                    onChange={(e) => handleSheetChange(e.target.value)}
                                >
                                    {(workbookInfo.sheets || []).map((sheet) => (
                                        <MenuItem key={sheet.name} value={sheet.name}>{sheet.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                                <InputLabel>Source column</InputLabel>
                                <Select
                                    label="Source column"
                                    value={sourceColumnIndex}
                                    onChange={(e) => {
                                        setSourceColumnIndex(e.target.value);
                                        resetAnalysis();
                                    }}
                                >
                                    {(currentSheet?.columns || []).map((column) => (
                                        <MenuItem key={column.index} value={column.index}>
                                            {column.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                                <InputLabel>Source language</InputLabel>
                                <Select
                                    label="Source language"
                                    value={sourceLanguage}
                                    onChange={(e) => handleSourceLanguageChange(e.target.value)}
                                >
                                    {LANGUAGES.map((language) => (
                                        <MenuItem key={language.code} value={language.code}>{language.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                                <InputLabel>Target languages</InputLabel>
                                <Select
                                    multiple
                                    label="Target languages"
                                    value={targetLanguages}
                                    onChange={handleTargetLanguagesChange}
                                    renderValue={(selected) => selected.map((code) => LANGUAGE_NAMES[code] || code).join(', ')}
                                >
                                    {availableTargets.map((language) => (
                                        <MenuItem key={language.code} value={language.code}>
                                            <Checkbox checked={targetLanguages.includes(language.code)} size="small" />
                                            <ListItemText primary={language.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {workbookInfo && (
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Button variant="contained" size="small" onClick={handleAnalyze} disabled={loading}>
                                Match termbase
                            </Button>
                            {loading && <CircularProgress size={20} />}
                            <Typography variant="caption" color="text.secondary">
                                Exact source-term matches only. Longer unmatched phrases stay empty.
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Paper>

            {analysis && (
                <Box sx={{ mt: 2 }}>
                    <Stack direction="row" gap={1} flexWrap="wrap" mb={1.5}>
                        <Chip size="small" label={`${analysis.rows?.length || 0} source rows`} />
                        <Chip size="small" label={`${ambiguousUnresolvedCount} choices needed`} />
                        <Chip size="small" label={`${noMatchCount} no matches`} />
                    </Stack>

                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 520 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, minWidth: 70 }}>Row</TableCell>
                                    <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>
                                        {LANGUAGE_NAMES[sourceLanguage] || sourceLanguage}
                                    </TableCell>
                                    {targetLanguages.map((language) => (
                                        <TableCell key={language} sx={{ fontWeight: 700, minWidth: 260 }}>
                                            {LANGUAGE_NAMES[language] || language}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(analysis.rows || []).map((row) => (
                                    <TableRow key={row.rowNumber} hover>
                                        <TableCell>{row.rowNumber}</TableCell>
                                        <TableCell>{row.sourceText}</TableCell>
                                        {targetLanguages.map((language) => {
                                            const candidates = row.candidatesByLanguage?.[language] || [];
                                            const selectedId = selections[row.rowNumber]?.[language] || '';

                                            if (candidates.length === 0) {
                                                return (
                                                    <TableCell key={language}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No termbase match
                                                        </Typography>
                                                    </TableCell>
                                                );
                                            }

                                            if (candidates.length === 1) {
                                                const candidate = candidates[0];
                                                return (
                                                    <TableCell key={language}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {candidate.translation}
                                                        </Typography>
                                                        {candidate.productName && candidate.productName !== 'N/A' && (
                                                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                                                {candidate.productName}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell key={language}>
                                                    <FormControl size="small" fullWidth>
                                                        <Select
                                                            displayEmpty
                                                            value={selectedId}
                                                            onChange={(e) => setChoice(row.rowNumber, language, e.target.value)}
                                                            renderValue={(value) => {
                                                                if (!value) return 'Choose approved variant…';
                                                                const candidate = candidates.find((item) => item.termId === value);
                                                                return candidate?.translation || 'Choose approved variant…';
                                                            }}
                                                        >
                                                            <MenuItem value="" disabled>Choose approved variant…</MenuItem>
                                                            {candidates.map((candidate) => (
                                                                <MenuItem key={candidate.termId} value={candidate.termId}>
                                                                    <Box>
                                                                        <Typography variant="body2" fontWeight={700}>
                                                                            {candidate.translation}
                                                                        </Typography>
                                                                        {candidate.productName && candidate.productName !== 'N/A' && (
                                                                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                                                                {candidate.productName}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleExport}
                            disabled={loading || ambiguousUnresolvedCount > 0}
                        >
                            Download translated Excel
                        </Button>
                        {ambiguousUnresolvedCount > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Resolve {ambiguousUnresolvedCount} variant choice{ambiguousUnresolvedCount === 1 ? '' : 's'} first.
                            </Typography>
                        )}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

export default ExcelTermbaseBatch;
